import { formatCents } from '@core/money'
import type { Payable } from '@core/payments'
import { Card, Section } from '@design/components'
import { Tags } from '@design/Tags'

/**
 * The bills you know are coming but have not arrived.
 *
 * Kept apart from everything above it, and out of the openstaand total,
 * because spec.md §10 is firm that Estimated and Committed money are never
 * mixed — a forecast that quietly includes guesses is worse than no forecast.
 * These are not decisions waiting to be made; they are things being waited
 * for, which is why nothing here is tappable and nothing offers to pay.
 */
export function ExpectedSection({ items }: { items: Payable[] }) {
  if (items.length === 0) return null
  const total = items.reduce((sum, i) => sum + i.amountCents, 0)

  return (
    <Section title="Verwacht" count={items.length}>
      <p className="text-xs text-muted mb-2 px-1">
        Geschat, nog geen factuur · {formatCents(total)}
      </p>
      <div className="space-y-2">
        {items.map((item) => (
          <Card key={item.instalmentId} className="flex items-center gap-3 border-dashed">
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{item.supplierName ?? item.description}</p>
              <p className="text-sm text-muted">rond {formatDate(item.dueDate)} · schatting</p>
              <Tags
                spaceName={item.spaceName}
                spaceKind={item.spaceKind}
                category={item.category}
              />
            </div>
            <span className="shrink-0 tabular-nums font-medium text-muted">
              ± {formatCents(item.amountCents)}
            </span>
          </Card>
        ))}
      </div>
    </Section>
  )
}

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('nl-BE', { day: 'numeric', month: 'short' }).format(
    new Date(`${iso}T00:00:00`),
  )
}
